import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from 'shared/lib/utils';

export interface FormSelectOption {
  value: string;
  label: string;
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: FormSelectOption[];
  error?: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
}

const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  ({ label, options, error, hint, required, className, id, placeholder, ...rest }, ref) => {
    const { t } = useTranslation();
    const inputId = id || `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const errorMessage = error ? t(`validation.${error}`, { defaultValue: error }) : undefined;

    return (
      <div className="flex flex-col gap-2 w-full" data-error={error ? 'true' : undefined}>
        <label htmlFor={inputId} className="text-base font-medium text-gray-700">
          {label}
          {required && <span className="text-gray-500 ms-1">*</span>}
        </label>
        <div className="relative">
          <select
            id={inputId}
            ref={ref}
            aria-invalid={!!error}
            className={cn('input-modern appearance-none truncate pe-10', error && 'has-error', className)}
            {...rest}
          >
            {placeholder !== undefined && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18}
            className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
        </div>
        {errorMessage ? (
          <span className="text-xs font-medium text-red-600">{errorMessage}</span>
        ) : hint ? (
          <span className="text-xs text-gray-500">{hint}</span>
        ) : null}
      </div>
    );
  },
);

FormSelect.displayName = 'FormSelect';

export default FormSelect;
